import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { hasAnyEdges, type CoachAthleteEdge } from "@jackedlogs/shared";
import { Button, Card, TextField, resolveTheme, type UiTheme } from "@jackedlogs/ui";

import { useThemePalette } from "./theme";
import { useCoachEdgesWeb } from "./useCoachEdgesWeb";

// Web-rnw coach management (the capability mobile Settings has but web lacked).
// Coach-primary: invite an athlete by email, then manage the edge graph
// (accept / decline / revoke / cancel / leave). Grouping is the shared
// groupCoachEdges; the rows reuse the <Button> primitive + <Card>, no
// hand-rolled controls. Athlete-side accept/decline is symmetric so a user who
// is both coach and athlete manages every relationship here.

const shortSub = (sub: string): string =>
  sub.length > 12 ? `${sub.slice(0, 4)}…${sub.slice(-4)}` : sub;

type Action = { key: string; labelKey: string; path: "accept" | "revoke"; variant: "primary" | "danger" };

const ACCEPT_DECLINE: Action[] = [
  { key: "accept", labelKey: "coach.accept", path: "accept", variant: "primary" },
  { key: "decline", labelKey: "coach.decline", path: "revoke", variant: "danger" },
];

export function CoachSettingsScreen() {
  const { t } = useTranslation();
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const styles = makeStyles(theme);
  const { status, groups, callerSub, names, invite, act } = useCoachEdgesWeb();

  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<{ id: string; text: string } | null>(null);

  const onInvite = async () => {
    const value = email.trim();
    if (!value || inviting) return;
    setInviting(true);
    setInviteMsg(null);
    const r = await invite(value, note.trim());
    setInviting(false);
    if (r.ok) {
      setEmail("");
      setNote("");
      setInviteMsg({ ok: true, text: t("coach.inviteSent") });
    } else {
      setInviteMsg({
        ok: false,
        text: t(`coach.errCode.${r.code}`, { defaultValue: r.code ?? "io_error" }),
      });
    }
  };

  const onAct = async (edgeId: string, path: "accept" | "revoke") => {
    setBusyId(edgeId);
    setRowErr(null);
    const r = await act(edgeId, path);
    setBusyId(null);
    if (!r.ok) {
      setRowErr({
        id: edgeId,
        text: t(`coach.errCode.${r.code}`, { defaultValue: r.code ?? "io_error" }),
      });
    }
  };

  // Prefer the athlete's real name (resolved from their profile); fall back to
  // the invite email, then a short sub. Antenore's ask: show the name for now.
  const peerLabel = (edge: CoachAthleteEdge, peerSub: string): string =>
    names[peerSub] ??
    (edge.invitedEmail && edge.athleteSub === peerSub
      ? edge.invitedEmail
      : `${t("coach.subLabel")} ${shortSub(peerSub)}`);

  const renderGroup = (
    headingKey: string,
    edges: CoachAthleteEdge[],
    peerOf: (e: CoachAthleteEdge) => string,
    actions: Action[],
  ) =>
    edges.length > 0 ? (
      <View style={styles.group}>
        <Text style={styles.groupHeading}>{t(headingKey)}</Text>
        {edges.map((e) => (
          <View key={e.id} style={styles.row}>
            <View style={styles.rowMain}>
              <Text style={styles.peer} numberOfLines={1}>
                {peerLabel(e, peerOf(e))}
              </Text>
              {e.inviteNote ? (
                <Text style={styles.note} numberOfLines={2}>
                  “{e.inviteNote}”
                </Text>
              ) : null}
              {rowErr?.id === e.id ? (
                <Text style={styles.rowError}>{rowErr.text}</Text>
              ) : null}
            </View>
            <View style={styles.actions}>
              {actions.map((a) => (
                <Button
                  key={a.key}
                  theme={theme}
                  label={t(a.labelKey)}
                  variant={a.variant === "primary" ? "primary" : "danger"}
                  loading={busyId === e.id}
                  disabled={busyId === e.id}
                  // Vertical-only slop: adjacent Accept/Decline sit 4px apart, so
                  // the default 8px horizontal slop would overlap the sibling.
                  hitSlop={{ top: 8, bottom: 8 }}
                  onPress={() => onAct(e.id, a.path)}
                />
              ))}
            </View>
          </View>
        ))}
      </View>
    ) : null;

  if (!callerSub) return null;

  const anyContent = hasAnyEdges(groups);

  return (
    <View style={styles.wrap}>
      <Text style={styles.heading}>{t("coach.title")}</Text>
      <Text style={styles.subtitle}>{t("coach.subtitle")}</Text>

      <Card theme={theme}>
        <Text style={styles.formLabel}>{t("coach.inviteAthleteLabel")}</Text>
        <TextField
          theme={theme}
          value={email}
          onChangeText={setEmail}
          placeholder={t("coach.invitePlaceholder")}
          autoCapitalize="none"
          keyboardType="email-address"
          accessibilityLabel={t("coach.inviteAthleteLabel")}
        />
        <View style={styles.formGap} />
        <TextField
          theme={theme}
          value={note}
          onChangeText={setNote}
          placeholder={t("coach.inviteNotePlaceholder")}
          accessibilityLabel={t("coach.inviteNotePlaceholder")}
        />
        <View style={styles.formGap} />
        <Button
          theme={theme}
          label={inviting ? t("coach.inviteSending") : t("coach.inviteSend")}
          onPress={onInvite}
          loading={inviting}
          disabled={inviting || email.trim().length === 0}
        />
        {inviteMsg ? (
          <Text style={inviteMsg.ok ? styles.inviteOk : styles.rowError}>
            {inviteMsg.text}
          </Text>
        ) : null}
      </Card>

      {renderGroup("coach.pendingInLabel", groups.coachesPendingIn, (e) => e.coachSub, ACCEPT_DECLINE)}
      {renderGroup("coach.pendingInLabel", groups.athletesPendingIn, (e) => e.athleteSub, ACCEPT_DECLINE)}
      {renderGroup("coach.athletesHeading", groups.athletesActive, (e) => e.athleteSub, [
        { key: "revoke", labelKey: "coach.revoke", path: "revoke", variant: "danger" },
      ])}
      {renderGroup("coach.coachesHeading", groups.coachesActive, (e) => e.coachSub, [
        { key: "leave", labelKey: "coach.leave", path: "revoke", variant: "danger" },
      ])}
      {renderGroup("coach.pendingOutLabel", groups.athletesPendingOut, (e) => e.athleteSub, [
        { key: "cancel", labelKey: "coach.cancel", path: "revoke", variant: "danger" },
      ])}
      {renderGroup("coach.pendingOutLabel", groups.coachesPendingOut, (e) => e.coachSub, [
        { key: "cancel", labelKey: "coach.cancel", path: "revoke", variant: "danger" },
      ])}

      {status === "loading" ? (
        <Text style={styles.empty}>{t("coach.loading")}</Text>
      ) : status === "error" ? (
        <Text style={styles.rowError}>{t("coach.loadFailed")}</Text>
      ) : !anyContent ? (
        <Text style={styles.empty}>{t("coach.athletesEmpty")}</Text>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s3, padding: s.s4 },
    heading: { color: p.ink, fontSize: ty.sizes.lg, fontWeight: "700" },
    subtitle: { color: p.ink3, fontSize: ty.sizes.sm },
    formLabel: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      marginBottom: s.s2,
    },
    formGap: { height: s.s3 },
    inviteOk: { color: p.complete, fontSize: ty.sizes.sm, marginTop: s.s2 },
    group: { gap: s.s2, marginTop: s.s2 },
    groupHeading: {
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.6,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
      paddingVertical: s.s2,
      borderTopWidth: 1,
      borderTopColor: p.border,
    },
    rowMain: { flex: 1, minWidth: 0, gap: 2 },
    peer: { color: p.ink, fontSize: ty.sizes.base },
    note: { color: p.ink3, fontSize: ty.sizes.xs, fontStyle: "italic" },
    rowError: { color: p.danger, fontSize: ty.sizes.xs, marginTop: s.s2 },
    actions: { flexDirection: "row", gap: s.s2, alignItems: "center", flexShrink: 0 },
    empty: { color: p.ink3, fontSize: ty.sizes.sm, paddingVertical: s.s2 },
  });
}
